import React, { useState, useEffect, useRef, useCallback } from 'react';
import MensajeError from '../UI/MensajeError.js';
import BotonBuscarChico from '../UI/botones/BotonBuscarChico.js';
import CampoComboPais from '../UI/campos/CampoComboPais.js';
import CampoComboParametro from '../UI/campos/CampoComboParametro.js';
import CampoEditableTexto from '../UI/campos/CampoEditableTexto.js';
import ComboSelector, { TipoClave } from '../UI/campos/ComboSelector.js';
import PopupCancelable from '../UI/PopupCancelable.js';
import { encontrarIdNumber, EsArrayVacio } from '../funciones/FuncionesArray.js';
import * as TipoEquipo from '../constantes/TipoEquipo.js';
import * as Categorias from '../constantes/Categorias.js';
import * as TipoParametro from '../constantes/TipoParametro.js';
import useGetListaURL from '../../hooks/useGetListaURL.js';
import * as Data from '../data/DataEquipos.js';
import styles from './PopupBuscarEquipo.module.css';
import { NombreEquipo } from '../funciones/FuncionesEquipo.js';

const ITEM_TIPO_VACIO = { codigo: null, descripcion: '(Todos)' };
const ITEM_CATEGORIA_MAYOR = { codigo: Categorias.MAYOR, descripcion: 'Mayor' };

const PopupBuscarEquipo = (props) => {
  const visible = props.visible;
  const textoIn = props.texto;
  const prefijo = props.prefijo;
  const objetoFiltro = props.objetoFiltro || null;
  const tipoInicial = props.tipoInicial || ITEM_TIPO_VACIO;
  const tieneEquipoSeleccionado = props.tieneEquipoSeleccionado || false;
  const [equipoFocus, setEquipoFocus] = useState(null);
  const [paisBuscar, setPaisBuscar] = useState(null);
  const [tipoBuscar, setTipoBuscar] = useState(tipoInicial);
  const [textoBuscar, setTextoBuscar] = useState('');
  const [categoriaBuscar, setCategoriaBuscar] = useState(ITEM_CATEGORIA_MAYOR);
  const [equipos, setEquipos] = useState(null);
  const [error, setError] = useState(null);
  const [listaTipos, setListaTipos] = useState([ITEM_TIPO_VACIO]);
  const buttonReferences = useRef([]);
  const estabaVisibleRef = useRef(false);
  const { fetchData: fetchTipos, resetErroresGetLista } = useGetListaURL();

  useEffect(() => {
    resetErroresGetLista();
  }, [resetErroresGetLista]);

  useEffect(() => {
    fetchTipos('parametros/tipos_equipo', (data) => {
      setListaTipos([ITEM_TIPO_VACIO, ...(data || [])]);
    });
  }, [fetchTipos]);

  // Actualizar tipo cuando cambia tipoInicial
  useEffect(() => {
    if (tipoInicial && tipoInicial.codigo) {
      setTipoBuscar(tipoInicial);
    }
  }, [tipoInicial]);

  const busquedaHabilitada = (texto, pais, tipo) => {
    const tipoNoClub = tipo && tipo.codigo && tipo.codigo !== TipoEquipo.CLUB;
    return tipoNoClub || (texto && texto.length >= 3) || (pais && pais.id && pais.id !== '');
  };

  const buscarEquipos = useCallback(
    async (pais, tipo, texto, categoria) => {
      setError(null);
      if (busquedaHabilitada(texto, pais, tipo)) {
        try {
          const result = await Data.GetEquipos(prefijo, objetoFiltro, pais, tipo, texto, categoria);
          if (result !== undefined && result !== null) {
            setEquipos(result);
          }
        } catch (e) {
          setError(e);
          setEquipos(null);
        }
      } else {
        setEquipos(null);
      }
    },
    [prefijo, objetoFiltro]
  );

  const vaciarForm = useCallback(() => {
    setEquipos(null);
    setEquipoFocus(null);
    setTextoBuscar('');
    setPaisBuscar(null);
    setTipoBuscar(tipoInicial || ITEM_TIPO_VACIO);
    setCategoriaBuscar(ITEM_CATEGORIA_MAYOR);
    setError(null);
  }, [tipoInicial]);

  // Inicializar valores solo al abrir el popup para no pisar lo que escribe el usuario.
  useEffect(() => {
    if (visible === true && !estabaVisibleRef.current && textoIn) {
      setTextoBuscar(textoIn);
      // Solo buscar si cumple las validaciones
      if (busquedaHabilitada(textoIn, paisBuscar, tipoBuscar)) {
        buscarEquipos(paisBuscar, tipoBuscar, textoIn, categoriaBuscar);
      }
    }
    estabaVisibleRef.current = visible;
  }, [paisBuscar, tipoBuscar, categoriaBuscar, visible, textoIn, buscarEquipos]);

  // Seleccionar automáticamente si hay un solo resultado (solo cuando no hay equipo seleccionado)
  useEffect(() => {
    if (
      visible &&
      !tieneEquipoSeleccionado &&
      equipos &&
      equipos.length === 1 &&
      !EsArrayVacio(equipos)
    ) {
      // Seleccionar automáticamente el único equipo encontrado
      props.onEquipoElegido(equipos[0]);
      vaciarForm();
    }
  }, [equipos, visible, tieneEquipoSeleccionado, props, vaciarForm]);

  const onCampoChange = (nombre, valor) => {
    setTextoBuscar(valor);
    setEquipoFocus(null);
  };

  const onPaisElegido = (nombre, paisElegido) => {
    const pais = paisElegido && paisElegido.id ? paisElegido : null;
    setPaisBuscar(pais);
    buscarEquipos(pais, tipoBuscar, textoBuscar, categoriaBuscar);
  };

  const onTipoElegido = (tipoElegido) => {
    // ITEM_TIPO_VACIO has codigo: null — treat as no filter
    const tipo = tipoElegido && tipoElegido.codigo ? tipoElegido : ITEM_TIPO_VACIO;
    setTipoBuscar(tipo);
    buscarEquipos(paisBuscar, tipo.codigo ? tipo : null, textoBuscar, categoriaBuscar);
  };

  const onCategoriaElegida = (nombre, categoriaElegida) => {
    setCategoriaBuscar(categoriaElegida);
    buscarEquipos(paisBuscar, tipoBuscar.codigo ? tipoBuscar : null, textoBuscar, categoriaElegida);
  };

  const onClickBuscar = () => {
    buscarEquipos(paisBuscar, tipoBuscar.codigo ? tipoBuscar : null, textoBuscar, categoriaBuscar);
  };

  const equipoClickHandler = (event) => {
    event.preventDefault();
    const equipo = encontrarIdNumber(equipos, event.target.id);
    if (equipo) {
      props.onEquipoElegido(equipo);
      vaciarForm();
    }
  };

  const equipoFocusHandler = (equipo) => {
    setEquipoFocus(equipo);
  };

  const onAceptar = () => {
    if (equipoFocus) {
      props.onEquipoElegido(equipoFocus);
      vaciarForm();
    } else {
      alert('Seleccione un equipo');
    }
  };

  const onCancelar = () => {
    props.onCancelar();
    vaciarForm();
  };

  const mensajeListaVacia = () => {
    if (
      busquedaHabilitada(textoBuscar, paisBuscar, tipoBuscar) &&
      equipos &&
      equipos.length === 0
    ) {
      return 'No se encontraron equipos';
    }
    return 'Ingrese al menos tres caracteres del nombre, seleccione un país o seleccione un tipo de equipo que no sea Club para buscar';
  };

  let lista = [];
  if (equipos && busquedaHabilitada(textoBuscar, paisBuscar, tipoBuscar)) {
    for (const equipo of equipos) {
      lista = [...lista, equipo];
      if (lista.length === 10) {
        break;
      }
    }
  }

  return (
    <PopupCancelable
      visible={visible}
      texto=""
      height="22rem"
      width="30rem"
      mostrarEliminar={false}
      isGrabando={false}
      onAceptar={onAceptar}
      onCancelar={onCancelar}
    >
      <MensajeError errores={error} />
      <div className={`${styles.cuadroBusqueda}`}>
        <div className={`${styles.filaBusqueda}`}>
          <span className={`${styles.textoBusqueda}`}>Nombre:</span>
          <CampoEditableTexto
            nombre="buscar"
            valor={textoBuscar}
            largo={50}
            editar={true}
            estilo={`${styles.campoNombre}`}
            onChange={onCampoChange}
            onPressEnter={onClickBuscar}
          />
        </div>
        <div className={`${styles.filaBusqueda}`}>
          <span className={`${styles.textoBusqueda}`}>Tipo:</span>
          <ComboSelector
            lista={listaTipos}
            valorActual={tipoBuscar}
            campoClave={TipoClave.CODIGO}
            campoDescripcion="descripcion"
            isCargando={false}
            estilo={`${styles.campoTipo}`}
            onItemElegido={onTipoElegido}
          />
        </div>
        <div className={`${styles.filaBusqueda}`}>
          <span className={`${styles.textoBusqueda}`}>País:</span>
          <CampoComboPais
            nombre="pais"
            valor={paisBuscar}
            editar={true}
            incluirNulo={true}
            estilo={`${styles.campoPais}`}
            onChange={onPaisElegido}
          />
        </div>
        <div className={`${styles.filaBusqueda}`}>
          <span className={`${styles.textoBusqueda}`}>Categoría:</span>
          <CampoComboParametro
            nombre="categoria"
            valor={categoriaBuscar}
            editar={true}
            tipoParametro={TipoParametro.CATEGORIA}
            incluirNulo={false}
            estilo={`${styles.campoCategoria}`}
            onChange={onCategoriaElegida}
          />
          <BotonBuscarChico estilo={`${styles.boton}`} onClick={onClickBuscar} />
        </div>
      </div>
      <div className={`${styles.listaEquipos}`}>
        {lista.length === 0 && (
          <span className={`${styles.listaVacia}`}>{mensajeListaVacia()}</span>
        )}
        <ul>
          {lista.map((equipo, index) => (
            <li key={equipo.id} id={equipo.id}>
              <button
                className={`${styles.itemEquipo}`}
                key={equipo.id}
                ref={(element) => (buttonReferences.current[index] = element)}
                id={equipo.id}
                onClick={equipoClickHandler}
                onFocus={() => equipoFocusHandler(equipo)}
              >
                {NombreEquipo(equipo)}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </PopupCancelable>
  );
};

export default PopupBuscarEquipo;
